import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { listChangeSets } from "../../api/content/changeSetsApi";
import type { ChangeSetSummary } from "../../api/content/reviewDecoders";
import { isAllowed } from "../../api/access/decisions";
import { chosenProjectDecision, useProjects } from "../../projects/projectContext";
import { useSession } from "../../session/sessionContext";
import { useLoad } from "../../shared/loading/useLoad";
import { FORM_COMPACT_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../ui/styles/formStyles";
import type { ChangeSetChoice } from "./changeSetChoice";
import { readableChangeSetTitle } from "./changeSetTitle";
import FieldGroup from "../../ui/forms/FieldGroup";
import OptionLabel from "../../ui/forms/OptionLabel";
import LoadStatus from "../../ui/feedback/LoadStatus";

/**
 * Change sets that still take commits: open, or sent back. The user's own come first; a
 * curator with `propose_change` may also commit to someone else's, as the server allows
 * (v1's reviewer edits; curation-ui.md, curator edits).
 */
async function editableChangeSets(
  projectId: string,
  userId: string | null,
  includeOthers: boolean,
  signal: AbortSignal,
): Promise<{ readonly own: readonly ChangeSetSummary[]; readonly others: readonly ChangeSetSummary[] }> {
  const [open, sentBack] = await Promise.all([listChangeSets(projectId, "open", signal), listChangeSets(projectId, "sent_back", signal)]);
  const all = [...open, ...sentBack];
  return {
    own: all.filter((changeSet) => changeSet.openedBy === userId),
    others: includeOthers ? all.filter((changeSet) => changeSet.openedBy !== userId) : [],
  };
}

interface ChangeSetPickerProps {
  projectId: string;
  choice: ChangeSetChoice;
  onChange: (choice: ChangeSetChoice) => void;
  /** The title a new change set gets unless the user changes it. */
  defaultTitle: string;
}

/** Where a proposal goes: a new change set, the user's open one, or (for a curator)
 * someone else's open one. */
export default function ChangeSetPicker({ projectId, choice, onChange, defaultTitle }: ChangeSetPickerProps) {
  const { t } = useTranslation();
  const { session } = useSession();
  const projects = useProjects();
  const mayPropose = isAllowed(chosenProjectDecision(projects, "propose_change"));
  const userId = session.status === "signedIn" ? session.session.account.userId : null;
  const load = useCallback((signal: AbortSignal) => editableChangeSets(projectId, userId, mayPropose, signal), [projectId, userId, mayPropose]);
  const { state, reload } = useLoad(load);
  const own = state.status === "loaded" ? state.value.own : [];
  const others = state.status === "loaded" ? state.value.others : [];
  const available = [...own, ...others];

  return (
    <FieldGroup legend={t("ontologyViewer.proposals.target")} className="space-y-2">
      <OptionLabel>
        <input
          type="radio"
          checked={choice.kind === "new"}
          onChange={() => {
            onChange({ kind: "new", title: defaultTitle });
          }}
        />
        {t("ontologyViewer.proposals.newChangeSet")}
      </OptionLabel>
      {choice.kind === "new" ? (
        <input
          className={FORM_COMPACT_CONTROL_CLASS}
          aria-label={t("ontologyViewer.proposals.title")}
          value={choice.title}
          maxLength={200}
          onChange={(event) => {
            onChange({ kind: "new", title: event.target.value });
          }}
        />
      ) : null}
      <OptionLabel>
        <input
          type="radio"
          checked={choice.kind === "existing"}
          disabled={available.length === 0}
          onChange={() => {
            const first = available[0];
            if (first !== undefined) {
              onChange({ kind: "existing", changeSetId: first.changeSetId });
            }
          }}
        />
        {t("ontologyViewer.proposals.existingChangeSet")}
      </OptionLabel>
      <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} compact />
      {state.status === "loaded" && available.length === 0 ? (
        <p className="app-text-muted text-xs">{t("ontologyViewer.proposals.noOpenChangeSets")}</p>
      ) : null}
      {choice.kind === "existing" ? (
        <select
          className={FORM_SELECT_CLASS}
          aria-label={t("ontologyViewer.proposals.existingChangeSet")}
          value={choice.changeSetId}
          onChange={(event) => {
            onChange({ kind: "existing", changeSetId: event.target.value });
          }}
        >
          <optgroup label={t("ontologyViewer.proposals.ownChangeSets")}>
            {own.map((changeSet) => (
              <option key={changeSet.changeSetId} value={changeSet.changeSetId}>
                {readableChangeSetTitle(changeSet.title)} ({t(`reviews.states.${changeSet.state}`)})
              </option>
            ))}
          </optgroup>
          {others.length > 0 ? (
            <optgroup label={t("ontologyViewer.proposals.othersChangeSets")}>
              {others.map((changeSet) => (
                <option key={changeSet.changeSetId} value={changeSet.changeSetId}>
                  {readableChangeSetTitle(changeSet.title)} ({t(`reviews.states.${changeSet.state}`)})
                </option>
              ))}
            </optgroup>
          ) : null}
        </select>
      ) : null}
    </FieldGroup>
  );
}
